// font-loader.js: UI font readiness gate.
//
// waitForFont() resolves once the Nunito face is ready (or 3s elapses,
// whichever first). Caller blocks the first deal on this so no card lands
// while text still renders in the system-sans fallback (synthesis §4.3
// "prevent mid-animation font swap").
//
// Failure modes the loader has to absorb:
//   • woff2 asset missing → document.fonts.load() rejects → resolve(false)
//   • document.fonts API absent (very old browsers / jsdom) → resolve(true)
//   • Slow 3G download   → 3s timeout → resolve(false), log warning
//
// Returns boolean: true = Nunito loaded, false = fallback in use.
// Callers should NOT throw on false, AC #3 mandates graceful degradation.

const DEFAULT_TIMEOUT_MS = 3_000;
const PROBE_SPEC = '16px Nunito';

/**
 * @param {{ timeoutMs?: number, doc?: Document, log?: (msg:string)=>void }} [opts]
 * @returns {Promise<boolean>}  true if Nunito loaded, false if fallback active
 */
export async function waitForFont(opts = {}) {
  const doc = opts.doc || (typeof document !== 'undefined' ? document : null);
  const log = opts.log || ((m) => console.warn('[font-loader]', m));
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  if (!doc || !doc.fonts || typeof doc.fonts.load !== 'function') {
    // No FontFace API, treat as loaded so we don't block forever.
    return true;
  }

  const loadP = doc.fonts.load(PROBE_SPEC).then((faces) => {
    // .load() resolves with an array of loaded FontFace objects. Empty
    // array means the family wasn't found in any @font-face rule (asset
    // missing).
    return Array.isArray(faces) && faces.length > 0;
  }).catch(() => false);

  const timeoutP = new Promise((resolve) => {
    setTimeout(() => resolve(null), timeoutMs);
  });

  const result = await Promise.race([loadP, timeoutP]);
  if (result === null) {
    log(`Nunito did not load within ${timeoutMs}ms — using fallback`);
    return false;
  }
  if (result === false) {
    log('Nunito asset missing or failed to load — using fallback');
  }
  return result;
}
